import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import Form from '../forms/Form';
import Card from '../forms/Card';
import Input from '../forms/Input';
import Copyright from '../layout/Copyright';
import Loading from '../Loading';
import Title from '../forms/Title';
import Row from '../layout/Row';
import { Regex } from '../utility/regex';
import PhoneNumberInput from '../forms/formComponents/PhoneNumberInput';
import HamQuest from '../api/HamQuestGiver';
import { PublicRoute } from '../api/QuestRoutes';
import Button from '../forms/Button';
import Heading from '../forms/Heading';
import { useParams } from 'react-router-dom';
import Select from '../forms/Select';

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100svh;
    background: ${({ theme }) => theme.body};
    position: relative;
    z-index: 10;

    animation: fallDown;
    animation-duration: 1s;
    animation-fill-mode: forwards;
`;
const Logo = styled.div`
    display: flex;
    justify-content: center;
    height: 6rem;

    background-image: url(${({ url }) => url});
    position: relative;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: contain;
`;

const CRight = styled(Copyright)`
    position: absolute;
    bottom: 0;
    right: 0;
`;

const KioskSignup = ({ ...props }) => {
    const { token } = useParams();

    const [isFetching, setIsFetching] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [errorMessage, setErrorMsg] = useState('No Token Provided');

    const [accountData, setAccountData] = useState(null);
    const [userToken, setUserToken] = useState(null);
    const [isCompleted, setIsCompleted] = useState({
        done: false,
        msg: ''
    });

    const [isMerging, setIsMerging] = useState(false);

    useLayoutEffect(() => {
        if (token) {
            setIsValid(true);
            setIsFetching(true);
            setUserToken(token);

            HamQuest.getQuery(PublicRoute.company, { token })
                .then(res => setAccountData(res))
                .catch(ex => {
                    setIsValid(false);
                    setErrorMsg(ex.errorMessage || 'Invalid Token Provided');
                })
                .finally(() => setIsFetching(false))
        }
    }, []);

    useEffect(() => {
        document.title = isMerging ? 'Account Merge' : 'Account Completion'
    }, [isMerging]);

    return (
        <Wrapper>
            <Card style={{ maxWidth: '80svw', maxHeight: '90svh', overflow: 'auto' }}>
                {
                    isValid ? isFetching ? <Loading /> :
                        <>
                            <Logo url={'data:image/png;base64,' + accountData.logo} />
                            <Title>{accountData.companyName}</Title>

                            {isCompleted.done ?
                                <>
                                    <span>{isCompleted.msg}</span>
                                    {(accountData.googlePlay || accountData.ios) &&
                                        <>
                                            <Heading>Download Our App!</Heading>
                                            {accountData.googlePlay && <Button href={accountData.googlePlay}>Google Play</Button>}
                                            {accountData.ios && <Button href={accountData.ios}>IOS</Button>}
                                        </>
                                    }
                                </> :
                                <>
                                    {isMerging ?
                                        <>
                                            <b>Email is already associated with an account.</b>
                                            <span>Please log in to merge the wash club to your account.</span>
                                        </> :
                                        <>
                                            <b>Finish your account set-up</b>
                                            <span>Complete your profile below to receive an emailed monthly invoice for your Wash Club.</span>
                                        </>
                                    }
                                    <Form
                                        inputs={{
                                            token: userToken,
                                            firstName: '',
                                            lastName: '',
                                            email: '',
                                            phoneNumber: accountData.phoneNumber || '',
                                            postalCode: '',
                                            password: '',
                                            confirmPassword: '',

                                            make: '',
                                            model: '',
                                            year: '',
                                            license: accountData.licensePlate || '',
                                            color: '',
                                            state: '',
                                        }}
                                        onSubmit={(e) => (
                                            HamQuest.post(isMerging ? PublicRoute.merge : PublicRoute.completion, { ...e })
                                                .then(res => {
                                                    setIsCompleted({
                                                        done: true,
                                                        msg: isMerging ?
                                                            `Thank you ${res.firstName}! Your account has been successfully merged! Log into your app to view your club.` :
                                                            `Thank you ${e.firstName}! Your account has been created successfully! Thanks for being a valued customer at ${accountData.companyName}!${res.hasRfid ? ' Please see the attendant to install your RFID tag.' : ''}`
                                                    })
                                                })
                                        )}

                                        text={isMerging ? 'Merge Account' : 'Complete Signup'}
                                        submitting='Completing'
                                        autoComplete='off'
                                    >
                                        {({ resetErrors }) => (
                                            <>
                                                <Input
                                                    label='Email'
                                                    name='email'
                                                    type='email'
                                                    validate={(e, setMsg, resolve) => {
                                                        if (e.match(Regex.match.email)) {
                                                            HamQuest.post(PublicRoute.uniqueEmail, {
                                                                token: userToken,
                                                                email: e
                                                            })
                                                                .then(res => {
                                                                    if (!res) {
                                                                        setIsMerging(true)
                                                                        resetErrors();
                                                                    } else {
                                                                        setIsMerging(false)
                                                                    }
                                                                    resolve(false);
                                                                })
                                                                .catch((ex) => {
                                                                    setMsg(ex.blob);
                                                                })
                                                        } else {
                                                            setMsg('Please enter a valid email.');
                                                            setIsMerging(false)
                                                            resolve(true)
                                                        }
                                                    }}
                                                    debounce={400}
                                                    required
                                                />

                                                {isMerging ?
                                                    <Input
                                                        label='Password'
                                                        name='password'
                                                        type='password'
                                                        required
                                                    /> :
                                                    <>
                                                        <Row wrap>
                                                            <Input
                                                                label='First Name'
                                                                name='firstName'
                                                                type='text'
                                                                sanitize={Regex.sanitize.name}
                                                                required
                                                            />
                                                            <Input
                                                                label='Last Name'
                                                                name='lastName'
                                                                type='text'
                                                                sanitize={Regex.sanitize.name}
                                                                required
                                                            />
                                                        </Row>
                                                        <Row wrap>
                                                            <PhoneNumberInput />
                                                            <Input
                                                                label='Postal Code'
                                                                name='postalCode'
                                                                type='text'
                                                                required
                                                            />
                                                        </Row>
                                                        <Row wrap>
                                                            <Input
                                                                label='Password'
                                                                name='password'
                                                                type='password'
                                                                validate={Regex.test.password}
                                                                required
                                                            />
                                                            <Input
                                                                label='Confirm Password'
                                                                name='confirmPassword'
                                                                type='password'
                                                                confirmation='password'
                                                                required
                                                            />
                                                        </Row>


                                                        <Row wrap>
                                                            <Input
                                                                name='make'
                                                                label='Make'
                                                                maxLength={20}
                                                                required
                                                            />
                                                            <Input
                                                                name='model'
                                                                label='Model'
                                                                maxLength={20}
                                                                required
                                                            />
                                                        </Row>
                                                        <Row wrap>
                                                            <Select
                                                                label='Year'
                                                                name='year'
                                                                options={Array.from({ length: 61 }, (_, i) => {
                                                                    const val = (new Date().getFullYear() + 2) - i;
                                                                    return ({
                                                                        value: val,
                                                                        label: val
                                                                    })
                                                                })}
                                                                required
                                                                nullable
                                                            />
                                                            <Select
                                                                label='State'
                                                                name='state'
                                                                required
                                                                options={accountData.states}
                                                                nullable
                                                            />
                                                            <Input
                                                                name='color'
                                                                label='Color'
                                                                maxLength={20}
                                                                required
                                                            />
                                                        </Row>

                                                        <Input
                                                            name='license'
                                                            label='License Plate'
                                                            required
                                                            maxLength={10}
                                                            sanitize={e => {
                                                                e = e.replace(/./g, (char) => char.toUpperCase());
                                                                e = e.replace(/[^a-z0-9]/gi, '');
                                                                return e;
                                                            }}
                                                            validate={/(\w|\d)+/g}
                                                            mask={(e) => e.toUpperCase()}
                                                        />
                                                    </>
                                                }
                                            </>
                                        )}
                                    </Form>
                                </>
                            }
                        </> : <span>{errorMessage}</span>
                }
            </Card>
            {/* <CRight /> */}
        </Wrapper >
    );
}

export default KioskSignup;
